<template>
  <div class="bool-card boolean-search backsearch-form">
    <!-- Card Header with actions -->
    <header class="card-header">
      <div class="d-flex align-items-center justify-content-between">
        <div class="d-flex align-items-center">
          <a class="go-back-icon" @click.prevent="$emit('cancel')"><i class="fal fa-chevron-left"></i></a>
          <h3>{{ isEditing ? $t('istarshine.backsearch.edit_request') : $t('istarshine.backsearch.new_request') }}</h3>
        </div>
        <div class="actions">
          <div @click="$emit('cancel')" :class="{ disabled: saving }">
            <i class="far fa-times"></i><span>{{ $t('istarshine.backsearch.cancel') }}</span>
          </div>
          <div
            v-if="!disabled"
            @click="onSave"
            :class="{ disabled: saving || queryValidityIsBusy }"
          >
            <i :class="saving ? 'fas fa-spinner rotate' : 'far fa-save'"></i><span>{{ $t('istarshine.backsearch.save') }}</span>
          </div>
        </div>
      </div>
    </header>

    <!-- Name & description (same row, no section title) -->
    <section class="form-section form-section-name-desc">
      <!--
        Copy-from dropdown — only shown when creating (not editing) and options exist.
        Options and current selection come from backsearch-create.vue via props.
        Selecting an item emits "copy-from-select" to the parent, which updates
        selectedCopyOption → seedForm → this component's seedForm watcher fills the form.
      -->
      <div v-if="!isEditing && copyFromOptions.length" class="copy-from-row">
        <label class="field-label">{{ $t('istarshine.backsearch.copy_from_label') }}</label>
        <multi-select
          :value="currentCopyOption"
          :options="copyFromOptions"
          :searchable="true"
          :allow-empty="false"
          :disabled="disabled"
          :placeholder="$t('istarshine.backsearch.copy_from_placeholder')"
          label="label"
          track-by="value"
          group-values="items"
          group-label="group"
          :group-select="false"
          class="input-select copy-from-multiselect"
          @input="$emit('copy-from-select', $event)"
        >
          <span slot="noResult">{{ $t('istarshine.backsearch.no_searches') }}</span>
        </multi-select>
      </div>
      <div class="name-desc-row">
        <div class="name-desc-col">
          <label class="field-label">{{ $t('istarshine.backsearch.name') }}</label>
          <input
            v-model="form.name"
            type="text"
            class="input-ui name-input"
            :class="{ 'field-error': fieldErrors.name }"
            :disabled="disabled"
            maxlength="255"
            :placeholder="$t('istarshine.backsearch.name_placeholder')"
            @blur="touchField('name')"
          />
          <span v-if="fieldErrors.name" class="inline-error">{{ fieldErrors.name }}</span>
        </div>
        <div class="name-desc-col">
          <label class="field-label">{{ $t('istarshine.backsearch.description') }}</label>
          <textarea
            v-model="form.description"
            class="input-ui description-textarea"
            :class="{ 'field-error': fieldErrors.description }"
            :disabled="disabled"
            maxlength="2000"
            rows="3"
            :placeholder="$t('istarshine.backsearch.description_placeholder')"
            @blur="touchField('description')"
          ></textarea>
          <span v-if="fieldErrors.description" class="inline-error">{{ fieldErrors.description }}</span>
        </div>
      </div>
    </section>

    <!-- Filters -->
    <section class="form-section">
      <header>
        <h4>{{ $t('istarshine.backsearch.filters') }}</h4>
      </header>
      
      <!-- User Filter Type Toggle -->
      <div class="filter-type-toggle">
        <label class="filter-type-label">
          <input type="radio" value="exclude" v-model="userFilterType" :disabled="disabled" />
          {{ $t('istarshine.backsearch.exclude_users') }}
        </label>
        <label class="filter-type-label">
          <input type="radio" value="include" v-model="userFilterType" :disabled="disabled" />
          {{ $t('istarshine.backsearch.include_users') }}
        </label>
      </div>

      <!-- Excluded Users (shown when userFilterType === 'exclude') -->
      <div v-if="userFilterType === 'exclude'" class="filter-group">
        <h5 class="filter-subheading">{{ $t('istarshine.backsearch.exclude_filter') }}</h5>
        <multi-select
          :value="form.excluded_users"
          :options="[]"
          :multiple="true"
          :taggable="true"
          :searchable="true"
          :close-on-select="false"
          :hide-selected="true"
          :allow-empty="true"
          :disabled="disabled"
          :placeholder="$t('istarshine.backsearch.excluded_users_placeholder')"
          :tag-placeholder="$t('istarshine.backsearch.excluded_users_tag_placeholder')"
          class="input-select user-multiselect"
          @tag="onExcludedTag"
          @input="onExcludedInput"
        />
        <span class="user-count">{{ (form.excluded_users || []).length }}/50</span>
        <span v-if="fieldErrors.excluded_users" class="inline-error">{{ fieldErrors.excluded_users }}</span>
        <small class="field-hint">{{ $t('istarshine.backsearch.excluded_users_hint') }}</small>
      </div>

      <!-- Included Users (shown when userFilterType === 'include') -->
      <div v-if="userFilterType === 'include'" class="filter-group">
        <h5 class="filter-subheading">{{ $t('istarshine.backsearch.include_filter') }}</h5>
        <multi-select
          :value="form.included_users"
          :options="[]"
          :multiple="true"
          :taggable="true"
          :searchable="true"
          :close-on-select="false"
          :hide-selected="true"
          :allow-empty="true"
          :disabled="disabled"
          :placeholder="$t('istarshine.backsearch.included_users_placeholder')"
          :tag-placeholder="$t('istarshine.backsearch.included_users_tag_placeholder')"
          class="input-select user-multiselect"
          @tag="onIncludedTag"
          @input="onIncludedInput"
        />
        <span class="user-count">{{ (form.included_users || []).length }}/50</span>
        <span v-if="fieldErrors.included_users" class="inline-error">{{ fieldErrors.included_users }}</span>
        <small class="field-hint">{{ $t('istarshine.backsearch.included_users_hint') }}</small>
      </div>

      <!-- Follower Count Filter -->
      <div class="filter-group follower-filter-group">
        <h5 class="filter-subheading">{{ $t('istarshine.backsearch.follower_count_filter') }}</h5>
        <div class="follower-filter-row">
          <div class="follower-field">
            <label class="field-label">{{ $t('istarshine.backsearch.follower_operator') }}</label>
            <select v-model="form.follower_count_operator" class="input-ui" :disabled="disabled">
              <option value="">{{ $t('istarshine.backsearch.no_follower_filter') }}</option>
              <option value="<">&lt; (less than)</option>
              <option value="<=">≤ (less than or equal)</option>
              <option value=">">&gt; (greater than)</option>
              <option value=">=">≥ (greater than or equal)</option>
              <option value="><">&gt;&lt; (between)</option>
            </select>
          </div>
          
          <div v-if="needsFollowerMin" class="follower-field">
            <label class="field-label">{{ $t('istarshine.backsearch.follower_min') }}</label>
            <input 
              v-model.number="form.follower_count_min" 
              type="number" 
              min="0" 
              class="input-ui"
              :class="{ 'field-error': fieldErrors.follower_count_min }"
              :disabled="disabled"
            />
          </div>
          
          <div v-if="needsFollowerMax" class="follower-field">
            <label class="field-label">{{ $t('istarshine.backsearch.follower_max') }}</label>
            <input 
              v-model.number="form.follower_count_max" 
              type="number" 
              min="0" 
              class="input-ui"
              :class="{ 'field-error': fieldErrors.follower_count_max }"
              :disabled="disabled"
            />
          </div>
        </div>
        <span v-if="fieldErrors.follower_count" class="inline-error">{{ fieldErrors.follower_count }}</span>
        <small class="field-hint">{{ $t('istarshine.backsearch.follower_filter_hint') }}</small>
      </div>
    </section>

    <!-- Query: search definition -->
    <section class="form-section form-section-query">
      <header>
        <h4>{{ $t('istarshine.backsearch.query_section') }}</h4>
      </header>

      <div class="query-field-group">
        <h5 class="query-field-title query-field-title-row">
          {{ $t('istarshine.backsearch.query') }}
          <i v-if="queryValidityIsBusy" class="fas fa-spinner rotate query-validating-spinner"></i>
        </h5>
        <div class="query-input-wrapper">
          <textarea
            v-model="form.query"
            :placeholder="$t('istarshine.backsearch.query_placeholder')"
            maxlength="100"
            class="input-ui query-textarea"
            :class="{ 'field-error': queryFieldHasError }"
            :disabled="disabled"
            rows="2"
            @input="onQueryInput"
            @blur="onQueryBlur"
          ></textarea>
          <span class="char-count" :class="{ 'over-limit': (form.query || '').length > 100 }">
            {{ (form.query || '').length }}/100
          </span>
        </div>
        <span v-if="fieldErrors.query" class="inline-error">{{ fieldErrors.query }}</span>
        <div
          v-else-if="!queryApiValidation.isValid && queryApiValidation.errorMessage"
          class="query-validation-message error"
        >
          {{ queryApiValidation.errorMessage }}
        </div>
      </div>

      <div class="query-field-group query-field-group-max-providers">
        <div class="max-post-col">
          <h5 class="query-field-title">{{ $t('istarshine.backsearch.max_post_count') }}</h5>
          <input
            v-model.number="form.max_post_count"
            type="number"
            min="1"
            max="5000"
            class="input-ui max-posts-input"
            :class="{ 'field-error': fieldErrors.max_post_count }"
            :disabled="disabled"
            @blur="touchField('max_post_count')"
          />
          <span v-if="fieldErrors.max_post_count" class="inline-error">{{ fieldErrors.max_post_count }}</span>
          <small class="field-hint">{{ $t('istarshine.backsearch.max_post_count_hint') }}</small>
        </div>
        <div class="providers-col">
          <h5 class="query-field-title">{{ $t('istarshine.backsearch.providers') }}</h5>
          <div class="provider-checkboxes">
            <label class="provider-checkbox-label">
              <input
                type="checkbox"
                :checked="form.providers === null"
                :disabled="disabled"
                @change="toggleAllProviders"
              />
              {{ $t('istarshine.backsearch.all_providers') }}
            </label>
            <label
              v-for="p in availableProviders"
              :key="p"
              class="provider-checkbox-label"
            >
              <input
                type="checkbox"
                :value="p"
                :checked="isProviderSelected(p)"
                :disabled="disabled || form.providers === null"
                @change="onProviderToggle(p, $event)"
              />
              {{ $t('istarshine.' + p) }}
            </label>
          </div>
          <small class="field-hint">{{ $t('istarshine.backsearch.providers_hint') }}</small>
          <span v-if="fieldErrors.providers" class="inline-error">{{ fieldErrors.providers }}</span>
        </div>
      </div>

      <div class="query-field-group">
        <h5 class="query-field-title">{{ $t('istarshine.backsearch.start_date') }} &amp; {{ $t('istarshine.backsearch.end_date') }}</h5>
        <div class="date-row">
          <div class="date-field">
            <label class="field-label">{{ $t('istarshine.backsearch.start_date') }}</label>
            <app-date-picker
              :value="form.start_date"
              format="YYYY-MM-DD"
              value-type="YYYY-MM-DD"
              :disabled="disabled"
              :clearable="false"
              :disabled-date="disabledStartDate"
              :class="{ 'field-error-picker': fieldErrors.start_date }"
              @input="(val) => { form.start_date = val; touchField('start_date'); }"
            />
            <span v-if="fieldErrors.start_date" class="inline-error">{{ fieldErrors.start_date }}</span>
            <small class="field-hint">{{ $t('istarshine.backsearch.start_date_hint') }}</small>
          </div>
          <div class="date-field">
            <label class="field-label">{{ $t('istarshine.backsearch.end_date') }}</label>
            <app-date-picker
              :value="form.end_date"
              format="YYYY-MM-DD"
              value-type="YYYY-MM-DD"
              :disabled="disabled"
              :clearable="false"
              :disabled-date="disabledEndDate"
              :class="{ 'field-error-picker': fieldErrors.end_date }"
              @input="(val) => { form.end_date = val; touchField('end_date'); }"
            />
            <span v-if="fieldErrors.end_date" class="inline-error">{{ fieldErrors.end_date }}</span>
          </div>
        </div>
        <small class="field-hint">{{ $t('istarshine.backsearch.date_hint') }}</small>
      </div>
    </section>

    <!-- Volume Remaining -->
    <div v-if="backsearchRemaining !== null" class="volume-remaining-line">
      <i class="fas fa-info-circle"></i>
      {{ $t('istarshine.backsearch.volume_remaining') }}: <strong>{{ backsearchRemaining }}</strong>
    </div>

    <!-- Bottom actions -->
    <footer class="card-footer">
      <button type="button" class="btn-footer btn-cancel" :disabled="saving" @click="$emit('cancel')">
        {{ $t('istarshine.backsearch.cancel') }}
      </button>
      <button
        v-if="!disabled"
        type="button"
        class="btn-footer btn-save"
        :disabled="saving || queryValidityIsBusy"
        @click="onSave"
      >
        <i v-if="saving" class="fas fa-spinner rotate"></i>
        {{ $t('istarshine.backsearch.save') }}
      </button>
    </footer>

  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import MultiSelect from 'vue-multiselect';
import DatePicker from 'vue2-datepicker';
import { normalizeFormFields } from '../../helpers/istarshine/backsearchTemplate';

// Complete list of all possible providers - used only as comprehensive reference
// Component receives availableProviders prop with project-specific enabled providers
const VALID_PROVIDERS = ['wechat_mention', 'douyin_mention', 'red_mention', 'weibo_mention', 'toutiao_mention'];
const MAX_EXCLUDED = 50;
const MAX_NAME_LEN = 255;
const MAX_DESCRIPTION_LEN = 2000;

function dateToYmd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function minStartDateYmd() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setFullYear(d.getFullYear() - 1);
  return dateToYmd(d);
}

function todayYmd() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return dateToYmd(d);
}

export default {
  name: 'BacksearchForm',
  components: {
    MultiSelect,
    'app-date-picker': DatePicker
  },
  props: {
    request: { type: Object, default: null },
    disabled: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
    backsearchRemaining: { type: Number, default: null },
    // Pre-filled form data produced by the parent when the user picks a copy-from option.
    // Watched below to stamp values into the form and reset validation.
    seedForm: { type: Object, default: null },
    // Providers enabled for this project (subset of all possible providers).
    // Used for provider toggle buttons and validation.
    availableProviders: { type: Array, default: () => [] },
    // Grouped option list for the copy-from dropdown, built by backsearch-create.vue.
    copyFromOptions: { type: Array, default: () => [] },
    // The currently selected copy-from option, controlled by the parent.
    currentCopyOption: { type: Object, default: null }
  },

  data() {
    return {
      form: this.buildForm(this.request),
      fieldErrors: {},
      touchedFields: {},
      queryApiValidation: { isValid: true, errorMessage: '' },
      userFilterType: this.determineUserFilterType(this.request)
    };
  },

  computed: {
    isEditing() {
      return !!(this.request && this.request.id);
    },
    ...mapState({
      query_validity: state => state.booleanSearch.query_validity
    }),
    queryValidityIsBusy() {
      return !!(this.query_validity && this.query_validity.isBusy);
    },
    queryFieldHasError() {
      return !!(this.fieldErrors.query || !this.queryApiValidation.isValid);
    },
    startDateMinYmd() {
      return minStartDateYmd();
    },
    maxDateYmd() {
      return todayYmd();
    },
    needsFollowerMin() {
      return ['>', '>=', '><'].includes(this.form.follower_count_operator);
    },
    needsFollowerMax() {
      return ['<', '<=', '><'].includes(this.form.follower_count_operator);
    }
  },

  watch: {
    request: {
      deep: true,
      handler(val) {
        this.form = this.buildForm(val);
        this.fieldErrors = {};
        this.touchedFields = {};
        this.queryApiValidation = { isValid: true, errorMessage: '' };
        this.userFilterType = this.determineUserFilterType(val);
      }
    },
    // Fires when the parent resolves a new seedForm (user selected a copy-from option).
    // Stamps the pre-filled fields, derives the user-filter toggle, and resets validation
    // so the form looks fresh rather than carrying over errors from a previous selection.
    seedForm(val) {
      if (this.isEditing || !val) return;
      this.form = { ...val };
      this.userFilterType = (val.excluded_users?.length > 0) ? 'exclude'
        : (val.included_users?.length > 0) ? 'include' : 'exclude';
      this.fieldErrors = {};
      this.touchedFields = {};
      this.queryApiValidation = { isValid: true, errorMessage: '' };
    },
    userFilterType(newVal) {
      if (newVal === 'exclude') {
        this.form.included_users = [];
      } else if (newVal === 'include') {
        this.form.excluded_users = [];
      }
    }
  },

  methods: {
    ...mapActions({
      validateQuery: 'booleanSearch/validateQuery'
    }),

    disabledStartDate(date) {
      const y = dateToYmd(date);
      return y < this.startDateMinYmd || y > this.maxDateYmd;
    },

    disabledEndDate(date) {
      return dateToYmd(date) > this.maxDateYmd;
    },

    buildForm(req) {
      const defaultMaxPostCount = this.backsearchRemaining || 5000;
      return normalizeFormFields(req?.id ? req : null, { defaultMaxPostCount });
    },

    determineUserFilterType(req) {
      if (!req || !req.id) return 'exclude';
      if (Array.isArray(req.excluded_users) && req.excluded_users.length > 0) return 'exclude';
      if (Array.isArray(req.included_users) && req.included_users.length > 0) return 'include';
      return 'exclude';
    },

    isProviderSelected(p) {
      if (this.form.providers === null) return true;
      return Array.isArray(this.form.providers) && this.form.providers.includes(p);
    },

    toggleAllProviders() {
      this.form.providers = this.form.providers === null ? [...this.availableProviders] : null;
      this.$set(this.fieldErrors, 'providers', '');
    },

    onProviderToggle(provider, event) {
      if (!Array.isArray(this.form.providers)) return;
      const checked = event.target.checked;
      const list = [...this.form.providers];
      if (checked && !list.includes(provider)) {
        list.push(provider);
      } else if (!checked) {
        const idx = list.indexOf(provider);
        if (idx !== -1) list.splice(idx, 1);
      }
      this.form.providers = list;
      this.$set(this.fieldErrors, 'providers', '');
    },

    onExcludedTag(tag) {
      const val = typeof tag === 'string' ? tag.trim() : '';
      if (!val) return;
      const current = this.form.excluded_users || [];
      if (current.length >= MAX_EXCLUDED) return;
      if (current.includes(val)) return;
      this.form.excluded_users = [...current, val];
    },

    onExcludedInput(val) {
      const list = (val || []).map(s => String(s).trim()).filter(Boolean);
      this.form.excluded_users = list.slice(0, MAX_EXCLUDED);
    },

    onIncludedTag(tag) {
      const val = typeof tag === 'string' ? tag.trim() : '';
      if (!val) return;
      const current = this.form.included_users || [];
      if (current.length >= MAX_EXCLUDED) return;
      if (current.includes(val)) return;
      this.form.included_users = [...current, val];
    },

    onIncludedInput(val) {
      const list = (val || []).map(s => String(s).trim()).filter(Boolean);
      this.form.included_users = list.slice(0, MAX_EXCLUDED);
    },

    onQueryInput() {
      this.queryApiValidation = { isValid: true, errorMessage: '' };
    },

    async onQueryBlur() {
      this.touchField('query');
      if (this.fieldErrors.query) return;
      const q = (this.form.query || '').trim();
      if (!q) {
        this.queryApiValidation = { isValid: true, errorMessage: '' };
        return;
      }
      await this.validateQueryWithApi(q);
    },

    async validateQueryWithApi(queryString) {
      const query = (queryString && String(queryString).trim()) || '';
      if (!query) {
        this.queryApiValidation = { isValid: true, errorMessage: '' };
        return true;
      }
      try {
        await this.validateQuery({
          type: 'regex_query',
          model: { regex_query: query },
          isTagSearch: false
        });
        const v = this.query_validity;
        this.queryApiValidation = {
          isValid: v.isValid,
          errorMessage: v.errorMessage || ''
        };
        return v.isValid;
      } catch (e) {
        this.queryApiValidation = {
          isValid: false,
          errorMessage: this.$t('booleanSearch.query_validation.generic_error')
        };
        return false;
      }
    },

    touchField(field) {
      this.$set(this.touchedFields, field, true);
      this.validateField(field);
    },

    validateField(field) {
      const t = (k) => this.$t('istarshine.backsearch.validation.' + k);
      let error = '';

      switch (field) {
        case 'name': {
          const n = (this.form.name || '').trim();
          if (!n) error = t('name_required');
          else if (n.length > MAX_NAME_LEN) error = t('name_max_length');
          break;
        }
        case 'description': {
          const d = this.form.description || '';
          if (d.length > MAX_DESCRIPTION_LEN) error = t('description_max_length');
          break;
        }
        case 'query': {
          const q = (this.form.query || '').trim();
          if (!q) error = t('query_required');
          else if (q.length > 100) error = t('query_max_length');
          break;
        }
        case 'max_post_count': {
          const mpc = this.form.max_post_count;
          if (!mpc && mpc !== 0) error = t('max_post_count_required');
          else if (mpc < 1 || mpc > 5000) error = t('max_post_count_range');
          else if (this.backsearchRemaining !== null && mpc > this.backsearchRemaining) {
            error = this.$t('istarshine.backsearch.quota_exceeded');
          }
          break;
        }
        case 'start_date':
          if (!this.form.start_date) error = t('start_date_required');
          else if (this.form.start_date > this.maxDateYmd) error = t('date_not_future');
          else if (this.form.end_date && this.form.start_date >= this.form.end_date) error = t('start_before_end');
          break;
        case 'end_date':
          if (!this.form.end_date) error = t('end_date_required');
          else if (this.form.end_date > this.maxDateYmd) error = t('date_not_future');
          else if (this.form.start_date && this.form.start_date >= this.form.end_date) error = t('start_before_end');
          break;
        case 'excluded_users': {
          const ex = this.form.excluded_users || [];
          if (ex.length > MAX_EXCLUDED) error = t('excluded_users_max');
          break;
        }
        case 'included_users': {
          const inc = this.form.included_users || [];
          if (inc.length > MAX_EXCLUDED) error = t('included_users_max');
          break;
        }
        case 'follower_count': {
          const op = this.form.follower_count_operator;
          if (!op) break;
          const min = this.form.follower_count_min;
          const max = this.form.follower_count_max;
          const isInvalidNumber = (input) => typeof input !== 'number' || Number.isNaN(input) || input < 0;
          if (op === '<' || op === '<=') {
            if (isInvalidNumber(max)){
              error = t('follower_max_required');
            }
          } else if (op === '>' || op === '>=') {
            if (isInvalidNumber(min)){
              error = t('follower_min_required');
            }
          } else if (op === '><') {
            if (isInvalidNumber(min)){
              error = t('follower_min_required');
            }
            else if (isInvalidNumber(max)){
              error = t('follower_max_required');
            }
            else if (min >= max) error = t('follower_min_less_than_max');
          }
          break;
        }
      }

      this.$set(this.fieldErrors, field, error);
      return error;
    },

    validate() {
      const fields = ['name', 'description', 'query', 'max_post_count', 'start_date', 'end_date', 'excluded_users', 'included_users', 'follower_count'];
      const errs = [];

      fields.forEach(f => {
        this.$set(this.touchedFields, f, true);
        const err = this.validateField(f);
        if (err) errs.push(err);
      });

      const t = (k) => this.$t('istarshine.backsearch.validation.' + k);
      let providersErr = '';
      if (Array.isArray(this.form.providers)) {
        if (this.form.providers.some(p => !this.availableProviders.includes(p))) {
          providersErr = t('providers_invalid');
          errs.push(providersErr);
        }
      }
      this.$set(this.fieldErrors, 'providers', providersErr);

      return errs;
    },

    async onSave() {
      const errs = this.validate();
      if (errs.length > 0) return;

      const q = (this.form.query || '').trim();
      if (q) {
        await this.validateQueryWithApi(q);
        if (!this.queryApiValidation.isValid) {
          return;
        }
      }

      const payload = {
        name: (this.form.name || '').trim(),
        description: (this.form.description || '').trim(),
        query: this.form.query.trim(),
        max_post_count: Number(this.form.max_post_count),
        providers: this.form.providers,
        excluded_users: (this.form.excluded_users || []).map(s => s.trim()).filter(Boolean),
        included_users: (this.form.included_users || []).map(s => s.trim()).filter(Boolean),
        follower_count_min: this.form.follower_count_min !== null ? this.form.follower_count_min : undefined,
        follower_count_max: this.form.follower_count_max !== null ? this.form.follower_count_max : undefined,
        follower_count_operator: this.form.follower_count_operator || undefined,
        start_date: this.form.start_date,
        end_date: this.form.end_date
      };

      this.$emit('save', payload);
    }
  }
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style lang="scss" scoped>
@import "../../styles/base/variables.scss";

.backsearch-form {
  padding: 1rem;
}

.card-header {
  margin-bottom: 1.6rem;

  h3 {
    font-size: 1.4rem;
    font-weight: 400;
    margin: 0;
  }
}

.go-back-icon {
  color: $gray-500;
  margin-right: 0.8rem;
  cursor: pointer;
  transition: color 0.25s ease-in;
  &:hover { color: $carma-teal; }
}

.actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  font-size: 0.8rem;

  > div {
    cursor: pointer;
    transition: background-color 0.35s ease-in;
    padding: 0.1rem 0.5rem;
    border-radius: $binary-radius * 2;

    &:hover {
      background-color: $gray-100;

      span, i { color: $carma-teal; }
    }

    &.disabled {
      pointer-events: none;
      opacity: 0.5;
    }
  }

  span, i {
    color: $gray-400;
    transition: color 0.25s ease-in;
  }

  span {
    font-style: italic;
    margin-bottom: 0;
    font-weight: 300;
    font-size: 0.8rem;
  }

  i {
    margin-right: 0.25rem;
    font-size: 1rem;
    transition: transform 0.2s linear;
  }
}

.form-section {
  padding: 1rem 1.5rem;

  > header {
    margin-bottom: 1rem;
    padding-bottom: 0.75rem;
    border-bottom: 1px solid $gray-100;

    h4 {
      color: $gray-700;
      margin: 0;
      font-size: 0.95rem;
      font-weight: 700;
    }
  }
}

.inline-error {
  display: block;
  color: $red;
  font-size: 0.8rem;
  margin-top: 0.25rem;
}

.field-error {
  border-color: $red !important;
  &:focus { border-color: $red !important; }
}

.field-error-picker :deep(.mx-input) {
  border-color: $red !important;
}

.field-label {
  display: block;
  font-weight: 400;
  margin-bottom: 0.35rem;
  font-size: 0.9rem;
  color: $gray-600;
}

.form-section-name-desc {
  padding-top: 0.5rem;
}

.name-desc-row {
  display: flex;
  align-items: flex-start;
  gap: 1.25rem;

  @media (max-width: 640px) {
    flex-direction: column;
  }
}

.copy-from-row {
  margin-bottom: 1rem;

  .copy-from-multiselect {
    .multiselect__tags {
      background-color: $white;
      border: 1px solid $gray-300;
      border-radius: 4px;
    }
    &.multiselect--active .multiselect__tags {
      border-color: $carma-teal;
    }
    &.multiselect--disabled .multiselect__tags {
      background-color: $gray-100;
      border-color: $gray-200;
    }
  }
}

.name-desc-col {
  flex: 1;
  min-width: 0;
}

.name-input {
  width: 100%;
  padding: 0.45rem 0.6rem;
  &:focus { border-color: $carma-teal; outline: none; }
  &:disabled { background: $gray-100; }
}

.description-textarea {
  width: 100%;
  resize: vertical;
  min-height: 72px;
  padding: 0.5rem 0.75rem;
  &:focus { border-color: $carma-teal; outline: none; }
  &:disabled { background: $gray-100; }
}

.filter-subheading {
  margin: 0 0 0.75rem;
  font-size: 0.9rem;
  font-weight: 400;
  color: $gray-600;
}

.form-section-query {
  .query-field-group {
    margin-bottom: 1.25rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .query-field-title {
    margin: 0 0 0.65rem;
    font-size: 0.9rem;
    font-weight: 400;
    color: $gray-600;
  }

  .query-field-title-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .query-validating-spinner {
    font-size: 0.85rem;
    color: $gray-400;
  }

  .query-field-group-max-providers {
    display: flex;
    align-items: flex-start;
    gap: 1.25rem;

    @media (max-width: 640px) {
      flex-direction: column;

      .max-posts-input {
        width: 100%;
        max-width: none;
      }
    }
  }

  .max-post-col {
    flex: 0 0 auto;
    min-width: 11rem;
  }

  .providers-col {
    flex: 1;
    min-width: 0;
  }
}

.query-validation-message {
  margin-top: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: $binary-radius;
  font-size: 0.8rem;

  &.error {
    background: lighten($red, 42%);
    color: darken($red, 10%);
  }
}

.field-hint {
  display: block;
  font-size: 0.8rem;
  color: $gray-500;
  margin-top: 0.25rem;
}

.query-input-wrapper {
  position: relative;
}

.query-textarea {
  width: 100%;
  resize: vertical;
  min-height: 48px;
  padding: 0.5rem 3rem 0.5rem 0.75rem;
  &:focus { border-color: $carma-teal; outline: none; }
  &:disabled { background: $gray-100; }
}

.char-count {
  position: absolute;
  bottom: 6px;
  right: 8px;
  font-size: 0.75rem;
  color: $gray-400;
  &.over-limit { color: $red; font-weight: 600; }
}

.max-posts-input {
  width: 160px;
  padding: 0.4rem 0.6rem;
  &:focus { border-color: $carma-teal; outline: none; }
  &:disabled { background: $gray-100; }
}

.provider-checkboxes {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 1.25rem;
}

.provider-checkbox-label {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.9rem;
  cursor: pointer;
  input[type="checkbox"] { margin: 0; }
}

.date-row {
  display: flex;
  gap: 1rem;
}

.date-field {
  flex: 1;

  :deep(.mx-datepicker) {
    width: 100%;
  }

  .mx-input-wrapper .mx-input {
    border: 1px solid $gray-300;
    border-radius: 4px;
    &:hover,
    &:focus {
      border-color: $carma-teal;
    }
  }

  .mx-datepicker-disabled .mx-input {
    background: $gray-100;
    border-color: $gray-200;
    &:hover { border-color: $gray-200; }
  }
}

.filter-type-toggle {
  display: flex;
  gap: 1.5rem;
  margin-bottom: 1.25rem;
}

.filter-type-label {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.9rem;
  cursor: pointer;
  input[type="radio"] { margin: 0; }
}

.filter-group {
  margin-top: 1rem;
}

.user-multiselect.multiselect {
  width: 100%;
  .multiselect__tags {
    background-color: $white;
    border: 1px solid $gray-300;
    border-radius: 4px;
    margin-bottom: 0;
  }
  &.multiselect--active .multiselect__tags {
    border-color: $carma-teal;
  }
  &.multiselect--disabled .multiselect__tags {
    background-color: $gray-100;
    border-color: $gray-200;
  }
}

.user-count {
  font-size: 0.8rem;
  color: $gray-500;
  margin-top: 0.25rem;
  display: block;
}

.follower-filter-group {
  margin-top: 1.5rem;
}

.follower-filter-row {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
}

.follower-field {
  flex: 1;
  min-width: 0;
  
  select.input-ui,
  input.input-ui {
    width: 100%;
    height: 38px;
    padding: 0 0.6rem;
    border: 1px solid $gray-300;
    border-radius: 4px;
    &:focus {
      border-color: $carma-teal;
      outline: none;
    }
    &:disabled {
      background: $gray-100;
      border-color: $gray-200;
    }
  }

  select.input-ui {
    min-width: 200px;
  }
}

.volume-remaining-line {
  padding: 0.6rem 1.5rem;
  background: lighten($carma-yellow, 20%);
  border-top: 1px solid lighten($carma-yellow, 10%);
  border-bottom: 1px solid lighten($carma-yellow, 10%);
  font-size: 0.9rem;
}

.card-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid $gray-200;
}

.btn-footer {
  padding: 0.45rem 1.25rem;
  border: none;
  border-radius: $binary-radius;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease, opacity 0.2s ease;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  i { margin-right: 0.3rem; }
}

.btn-cancel {
  background-color: $gray-200;
  color: $gray-600;

  &:hover:not(:disabled) {
    background-color: $gray-300;
  }
}

.btn-save {
  background-color: $carma-teal;
  color: $white;

  &:hover:not(:disabled) {
    background-color: darken($carma-teal, 8%);
  }
}

</style>
